import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import "./Navbar.css";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drop, setDrop] = useState(null); // 'survey' | 'quote' | null
  const location = useLocation();
  const navRef = useRef(null);

  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => setIsOpen((prev) => !prev);
  const closeAll = () => { setIsOpen(false); setDrop(null); };

  useEffect(() => { setIsOpen(false); setDrop(null); }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // close dropdowns on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setDrop(null);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const surveyLinks = [
    { to: "/services", label: "Our Services", icon: "bi-rulers" },
    { to: "/portfolio", label: "Portfolio", icon: "bi-images" },
    { to: "/why-choose-us", label: "Why Choose Us?", icon: "bi-patch-check-fill" },
  ];

  const quoteOptions = [
    { to: "/quote", label: "Land Survey Drafting", icon: "bi-rulers", desc: "Per-project quotation" },
    { to: "/estimation", label: "Estimation & Quantity Takeoff", icon: "bi-calculator-fill", desc: "Plans & free trial" },
  ];

  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`} ref={navRef}>
        <NavLink to="/" className="navbar-logo" onClick={closeAll}>
          <Logo size={40} className="logo-hover-spin" />
          <h1>CadLap</h1>
        </NavLink>

        <ul className="navbar-links">
          <li>
            <NavLink to="/" end className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
              Home
            </NavLink>
          </li>

          {/* Land Survey Drafting Services dropdown */}
          <li className={`nav-drop ${drop === "survey" ? "open" : ""}`}>
            <button
              type="button"
              className={`nav-link nav-drop-btn ${["/services", "/portfolio", "/why-choose-us"].includes(location.pathname) ? "active" : ""}`}
              onClick={() => setDrop(drop === "survey" ? null : "survey")}
              aria-expanded={drop === "survey"}
            >
              Land Survey Drafting Services <i className="bi bi-chevron-down"></i>
            </button>
            <div className="nav-drop-menu">
              {surveyLinks.map((l) => (
                <NavLink key={l.to} to={l.to} className="nav-drop-item" onClick={closeAll}>
                  <i className={`bi ${l.icon}`}></i> {l.label}
                </NavLink>
              ))}
            </div>
          </li>

          <li>
            <NavLink to="/estimation" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
              Estimation &amp; Quantity Takeoff
              <span className="nav-new-pill">New</span>
            </NavLink>
          </li>

          <li>
            <NavLink to="/contact" className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
              Contact Us
            </NavLink>
          </li>

          {/* Get a Quote — two options */}
          <li className={`nav-drop ${drop === "quote" ? "open" : ""}`}>
            <button
              type="button"
              className="nav-cta nav-drop-btn"
              onClick={() => setDrop(drop === "quote" ? null : "quote")}
              aria-expanded={drop === "quote"}
            >
              <i className="bi bi-envelope-paper-fill"></i> Get a Quote <i className="bi bi-chevron-down"></i>
            </button>
            <div className="nav-drop-menu nav-drop-menu-right">
              {quoteOptions.map((o) => (
                <Link key={o.to} to={o.to} className="nav-drop-item" onClick={closeAll}>
                  <i className={`bi ${o.icon}`}></i>
                  <span>
                    {o.label}
                    <small>{o.desc}</small>
                  </span>
                </Link>
              ))}
            </div>
          </li>

          <li>
            <a href="/estimator.html" className="nav-cta nav-cta-app">
              <i className="bi bi-box-arrow-in-right"></i> Launch App
            </a>
          </li>
        </ul>

        <button
          type="button"
          className={`navbar-hamburger ${isOpen ? "open" : ""}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isOpen}
        >
          <span className="line"></span>
          <span className="line"></span>
          <span className="line"></span>
        </button>
      </nav>

      <div
        className={`sidebar-backdrop ${isOpen ? "show" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      <aside className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <Logo size={32} />
          <span>CadLap</span>
        </div>
        <ul>
          <li>
            <NavLink to="/" end onClick={closeMenu} className={({ isActive }) => (isActive ? "active" : "")}>
              <i className="bi bi-house-door-fill" aria-hidden="true"></i>
              <span>Home</span>
            </NavLink>
          </li>

          <li className="sidebar-group-label">
            <i className="bi bi-rulers"></i> Land Survey Drafting Services
          </li>
          {surveyLinks.map((l) => (
            <li key={l.to} className="sidebar-sub">
              <NavLink to={l.to} onClick={closeMenu} className={({ isActive }) => (isActive ? "active" : "")}>
                <i className={`bi ${l.icon}`} aria-hidden="true"></i>
                <span>{l.label}</span>
              </NavLink>
            </li>
          ))}

          <li>
            <NavLink to="/estimation" onClick={closeMenu} className={({ isActive }) => (isActive ? "active" : "")}>
              <i className="bi bi-calculator-fill" aria-hidden="true"></i>
              <span>Estimation &amp; Quantity Takeoff</span>
              <span className="nav-new-pill">New</span>
            </NavLink>
          </li>

          <li>
            <NavLink to="/contact" onClick={closeMenu} className={({ isActive }) => (isActive ? "active" : "")}>
              <i className="bi bi-envelope-fill" aria-hidden="true"></i>
              <span>Contact Us</span>
            </NavLink>
          </li>

          <li className="sidebar-group-label">
            <i className="bi bi-envelope-paper-fill"></i> Get a Quote
          </li>
          {quoteOptions.map((o) => (
            <li key={"q" + o.to} className="sidebar-sub">
              <NavLink to={o.to} onClick={closeMenu} className={({ isActive }) => (isActive ? "active" : "")}>
                <i className={`bi ${o.icon}`} aria-hidden="true"></i>
                <span>{o.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="sidebar-footer">
          <a href="/estimator.html" className="sidebar-cta" style={{ marginBottom: "10px" }}>
            <i className="bi bi-box-arrow-in-right"></i> Launch Estimator App
          </a>
          <a href="mailto:info@cadlap.com" className="sidebar-cta">
            <i className="bi bi-envelope-paper-fill"></i> Email Us
          </a>
        </div>
      </aside>
    </>
  );
};

export default Navbar;
